import { c as _c } from "react/compiler-runtime";
import type { Dispatch, SetStateAction } from "react";
import { useMemo, useRef, useState } from "react";
import { useClickAway } from "react-use";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import type { ApiCreateGroup } from "@/generated/models/ApiCreateGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { validateGroupPayload } from "@/services/groups/groupMutations";
import { createInitialInlineGroupBuilderState, dedupeInlineIdentities, getInlineGroupDraftSummary, getInlineIdentityAddresses } from "./createWaveInlineGroupBuilder";
import type { CreateWaveInlineGroupBuilderState, CreateWaveInlineGroupPanel, CreateWaveInlineGroupRuleType } from "./createWaveInlineGroupBuilder";
const PANEL_ACTIONS: CreateWaveInlineGroupPanel = "actions";
const PANEL_IDENTITY: CreateWaveInlineGroupPanel = "identity";
const PANEL_RULE_LIST: CreateWaveInlineGroupPanel = "rule-list";
const PANEL_RULE_EDITOR: CreateWaveInlineGroupPanel = "rule-editor";
const PANEL_SEARCH: CreateWaveInlineGroupPanel = "search";
function getDisplayedBuilder({
  builder,
  disabled
}: {
  readonly builder: CreateWaveInlineGroupBuilderState;
  readonly disabled: boolean;
}): CreateWaveInlineGroupBuilderState {
  if (!disabled) {
    return builder;
  }
  return {
    ...builder,
    panel: PANEL_ACTIONS,
    activeRule: null
  };
}
function buildIdentityDraftState({
  current,
  identities
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly identities: readonly CommunityMemberMinimal[];
}): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    identities,
    draft: {
      ...current.draft,
      group: {
        ...current.draft.group,
        identity_addresses: getInlineIdentityAddresses(identities)
      }
    }
  };
}
function collapseInlineGroupBuilder(current: CreateWaveInlineGroupBuilderState): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    panel: PANEL_ACTIONS,
    activeRule: null
  };
}
function openInlineGroupPanel({
  current,
  panel
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly panel: CreateWaveInlineGroupPanel;
}): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    panel,
    activeRule: null
  };
}
function openInlineGroupRule({
  current,
  rule
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly rule: CreateWaveInlineGroupRuleType;
}): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    activeRule: rule,
    panel: PANEL_RULE_EDITOR
  };
}
function toggleInlineGroupRule({
  current,
  rule
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly rule: CreateWaveInlineGroupRuleType;
}): CreateWaveInlineGroupBuilderState {
  if (current.panel === PANEL_RULE_EDITOR && current.activeRule === rule) {
    return {
      ...current,
      activeRule: null,
      panel: PANEL_RULE_LIST
    };
  }
  return openInlineGroupRule({
    current,
    rule
  });
}
function clearInlineGroupDraft(current: CreateWaveInlineGroupBuilderState): CreateWaveInlineGroupBuilderState {
  const next = createInitialInlineGroupBuilderState();
  return {
    ...next,
    panel: current.panel === PANEL_SEARCH || current.panel === PANEL_ACTIONS ? PANEL_ACTIONS : current.panel,
    activeRule: current.panel === PANEL_RULE_EDITOR ? current.activeRule : null
  };
}
export type CreateWaveGroupInlinePanelProps = {
  readonly suggestedName: string;
  readonly defaultLabel: string;
  readonly disabled?: boolean;
  readonly selectedGroup: ApiGroupFull | null;
  readonly allowGroupClear?: boolean;
  readonly onChange: (group: ApiGroupFull | null) => void | Promise<void>;
  readonly onCreateGroup: (payload: ApiCreateGroup) => Promise<ApiGroupFull | null>;
};
function buildUnsavedGroupDescription(): string {
  return "Not applied yet.";
}
function useCreateWaveGroupInlinePanelViewState(t0) {
  const $ = _c(25);
  const {
    builder,
    defaultLabel,
    disabled,
    isCreating,
    selectedGroup
  } = t0;
  let t1;
  if ($[0] !== builder || $[1] !== disabled) {
    t1 = getDisplayedBuilder({
      builder,
      disabled
    });
    $[0] = builder;
    $[1] = disabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const displayedBuilder = t1;
  let t2;
  if ($[3] !== builder.draft || $[4] !== builder.identities.length) {
    t2 = getInlineGroupDraftSummary({
      draft: builder.draft,
      identityCount: builder.identities.length
    });
    $[3] = builder.draft;
    $[4] = builder.identities.length;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const draftSummary = t2;
  let t3;
  if ($[6] !== builder.draft) {
    t3 = validateGroupPayload(builder.draft);
    $[6] = builder.draft;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const validation = t3;
  const canCreateDraft = validation.valid && !disabled && !isCreating;
  const canResetDraft = !!draftSummary && !disabled && !isCreating;
  const isIdentityPanel = displayedBuilder.panel === PANEL_IDENTITY;
  const isRulePanel = displayedBuilder.panel === PANEL_RULE_LIST || displayedBuilder.panel === PANEL_RULE_EDITOR;
  const isSearchPanel = displayedBuilder.panel === PANEL_SEARCH;
  const hasUnsavedGroup = !!draftSummary && !disabled;
  const currentGroupLabel = selectedGroup?.name ?? defaultLabel;
  const t4 = displayedBuilder.panel !== PANEL_ACTIONS;
  const t5 = hasUnsavedGroup && !isSearchPanel;
  let t6;
  if ($[8] !== hasUnsavedGroup) {
    t6 = hasUnsavedGroup ? buildUnsavedGroupDescription() : null;
    $[8] = hasUnsavedGroup;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = hasUnsavedGroup ? draftSummary : null;
  let t8;
  if ($[10] !== canCreateDraft || $[11] !== canResetDraft || $[12] !== currentGroupLabel || $[13] !== displayedBuilder || $[14] !== draftSummary || $[15] !== hasUnsavedGroup || $[16] !== isIdentityPanel || $[17] !== isRulePanel || $[18] !== isSearchPanel || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== validation.valid) {
    t8 = {
      canCreateDraft,
      canResetDraft,
      currentGroupLabel,
      displayedBuilder,
      draftSummary,
      hasUnsavedGroup,
      isDraftValid: validation.valid,
      isExpandedPanel: t4,
      isIdentityPanel,
      isRulePanel,
      isSearchPanel,
      showDraftFooter: t5,
      unsavedGroupDescription: t6,
      unsavedGroupSummary: t7
    };
    $[10] = canCreateDraft;
    $[11] = canResetDraft;
    $[12] = currentGroupLabel;
    $[13] = displayedBuilder;
    $[14] = draftSummary;
    $[15] = hasUnsavedGroup;
    $[16] = isIdentityPanel;
    $[17] = isRulePanel;
    $[18] = isSearchPanel;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = validation.valid;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function useCreateWaveGroupInlinePanelController({
  allowGroupClear,
  builder,
  canCreateDraft,
  canResetDraft,
  onChange,
  onCreateGroup,
  setBuilder,
  setIsCreating,
  suggestedName
}: {
  readonly allowGroupClear: boolean;
  readonly builder: CreateWaveInlineGroupBuilderState;
  readonly canCreateDraft: boolean;
  readonly canResetDraft: boolean;
  readonly onChange: (group: ApiGroupFull | null) => void | Promise<void>;
  readonly onCreateGroup: (payload: ApiCreateGroup) => Promise<ApiGroupFull | null>;
  readonly setBuilder: Dispatch<SetStateAction<CreateWaveInlineGroupBuilderState>>;
  readonly setIsCreating: Dispatch<SetStateAction<boolean>>;
  readonly suggestedName: string;
}) {
  const resetBuilder = () => {
    setBuilder(createInitialInlineGroupBuilderState());
  };
  const collapseBuilderPanel = () => {
    setBuilder(collapseInlineGroupBuilder);
  };
  const setDraft = (draft: ApiCreateGroup) => {
    setBuilder(current => ({
      ...current,
      draft
    }));
  };
  const addIdentity = (identity: CommunityMemberMinimal) => {
    setBuilder(current => {
      const identities = dedupeInlineIdentities([...current.identities, identity]);
      return buildIdentityDraftState({
        current,
        identities
      });
    });
  };
  const removeIdentity = (wallet: string) => {
    const normalizedWallet = wallet.trim().toLowerCase();
    setBuilder(current => {
      const identities = current.identities.filter(identity => {
        const key = identity.wallet.trim().toLowerCase();
        return key !== normalizedWallet;
      });
      return buildIdentityDraftState({
        current,
        identities
      });
    });
  };
  const openPanel = (panel: CreateWaveInlineGroupPanel) => {
    setBuilder(current => openInlineGroupPanel({
      current,
      panel
    }));
  };
  const togglePanel = (panel: CreateWaveInlineGroupPanel, isActive: boolean) => {
    if (isActive) {
      collapseBuilderPanel();
      return;
    }
    openPanel(panel);
  };
  const openRule = (rule: CreateWaveInlineGroupRuleType) => {
    setBuilder(current => openInlineGroupRule({
      current,
      rule
    }));
  };
  const toggleRule = (rule: CreateWaveInlineGroupRuleType) => {
    setBuilder(current => toggleInlineGroupRule({
      current,
      rule
    }));
  };
  const createAndUse = async () => {
    if (!canCreateDraft) {
      return;
    }
    setIsCreating(true);
    try {
      const nextPayload: ApiCreateGroup = {
        ...builder.draft,
        name: suggestedName.trim() || "Wave Group"
      };
      const createdGroup = await onCreateGroup(nextPayload);
      if (!createdGroup) {
        return;
      }
      await onChange(createdGroup);
      resetBuilder();
    } finally {
      setIsCreating(false);
    }
  };
  const onCreateAndUse = () => {
    void createAndUse();
  };
  const onClearAll = () => {
    if (!canResetDraft) {
      return;
    }
    setBuilder(clearInlineGroupDraft);
  };
  const selectExistingGroup = async (group: ApiGroupFull | null) => {
    if (!group && !allowGroupClear) {
      return;
    }
    await onChange(group);
    if (group) {
      resetBuilder();
    }
  };
  const onExistingGroupSelect = (group: ApiGroupFull | null) => {
    void selectExistingGroup(group);
  };
  return {
    addIdentity,
    collapseBuilderPanel,
    onCancelPanel: collapseBuilderPanel,
    onClearAll,
    onCreateAndUse,
    onExistingGroupSelect,
    openRule,
    removeIdentity,
    setDraft,
    togglePanel,
    toggleRule
  };
}
export function useCreateWaveGroupInlinePanel(t0) {
  const $ = _c(43);
  const {
    suggestedName,
    defaultLabel,
    disabled: t1,
    selectedGroup,
    allowGroupClear: t2,
    onChange,
    onCreateGroup
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const allowGroupClear = t2 === undefined ? true : t2;
  const panelRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);
  const [builder, setBuilder] = useState(_temp);
  let t3;
  if ($[0] !== builder || $[1] !== defaultLabel || $[2] !== disabled || $[3] !== isCreating || $[4] !== selectedGroup) {
    t3 = {
      builder,
      defaultLabel,
      disabled,
      isCreating,
      selectedGroup
    };
    $[0] = builder;
    $[1] = defaultLabel;
    $[2] = disabled;
    $[3] = isCreating;
    $[4] = selectedGroup;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    canCreateDraft,
    canResetDraft,
    currentGroupLabel,
    displayedBuilder,
    draftSummary,
    hasUnsavedGroup,
    isDraftValid,
    isExpandedPanel,
    isIdentityPanel,
    isRulePanel,
    isSearchPanel,
    showDraftFooter,
    unsavedGroupDescription,
    unsavedGroupSummary
  } = useCreateWaveGroupInlinePanelViewState(t3);
  let t4;
  if ($[6] !== allowGroupClear || $[7] !== builder || $[8] !== canCreateDraft || $[9] !== canResetDraft || $[10] !== onChange || $[11] !== onCreateGroup || $[12] !== suggestedName) {
    t4 = {
      allowGroupClear,
      builder,
      canCreateDraft,
      canResetDraft,
      onChange,
      onCreateGroup,
      setBuilder,
      setIsCreating,
      suggestedName
    };
    $[6] = allowGroupClear;
    $[7] = builder;
    $[8] = canCreateDraft;
    $[9] = canResetDraft;
    $[10] = onChange;
    $[11] = onCreateGroup;
    $[12] = suggestedName;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const {
    addIdentity,
    collapseBuilderPanel,
    onCancelPanel,
    onClearAll,
    onCreateAndUse,
    onExistingGroupSelect,
    openRule,
    removeIdentity,
    setDraft,
    togglePanel,
    toggleRule
  } = useCreateWaveGroupInlinePanelController(t4);
  let t5;
  if ($[14] !== builder.panel || $[15] !== collapseBuilderPanel) {
    t5 = () => {
      if (builder.panel !== PANEL_ACTIONS) {
        collapseBuilderPanel();
      }
    };
    $[14] = builder.panel;
    $[15] = collapseBuilderPanel;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useClickAway(panelRef, t5);
  let t6;
  if ($[17] !== addIdentity || $[18] !== canCreateDraft || $[19] !== canResetDraft || $[20] !== currentGroupLabel || $[21] !== displayedBuilder || $[22] !== draftSummary || $[23] !== hasUnsavedGroup || $[24] !== isCreating || $[25] !== isDraftValid || $[26] !== isExpandedPanel || $[27] !== isIdentityPanel || $[28] !== isRulePanel || $[29] !== isSearchPanel || $[30] !== onCancelPanel || $[31] !== onClearAll || $[32] !== onCreateAndUse || $[33] !== onExistingGroupSelect || $[34] !== openRule || $[35] !== removeIdentity || $[36] !== setDraft || $[37] !== showDraftFooter || $[38] !== togglePanel || $[39] !== toggleRule || $[40] !== unsavedGroupDescription || $[41] !== unsavedGroupSummary) {
    t6 = {
      addIdentity,
      canCreateDraft,
      canResetDraft,
      currentGroupLabel,
      displayedBuilder,
      draftSummary,
      hasUnsavedGroup,
      isCreating,
      isDraftValid,
      isExpandedPanel,
      isIdentityPanel,
      isRulePanel,
      isSearchPanel,
      onCancelPanel,
      onClearAll,
      onCreateAndUse,
      onExistingGroupSelect,
      openRule,
      panelRef,
      removeIdentity,
      setDraft,
      showDraftFooter,
      togglePanel,
      toggleRule,
      unsavedGroupDescription,
      unsavedGroupSummary
    };
    $[17] = addIdentity;
    $[18] = canCreateDraft;
    $[19] = canResetDraft;
    $[20] = currentGroupLabel;
    $[21] = displayedBuilder;
    $[22] = draftSummary;
    $[23] = hasUnsavedGroup;
    $[24] = isCreating;
    $[25] = isDraftValid;
    $[26] = isExpandedPanel;
    $[27] = isIdentityPanel;
    $[28] = isRulePanel;
    $[29] = isSearchPanel;
    $[30] = onCancelPanel;
    $[31] = onClearAll;
    $[32] = onCreateAndUse;
    $[33] = onExistingGroupSelect;
    $[34] = openRule;
    $[35] = removeIdentity;
    $[36] = setDraft;
    $[37] = showDraftFooter;
    $[38] = togglePanel;
    $[39] = toggleRule;
    $[40] = unsavedGroupDescription;
    $[41] = unsavedGroupSummary;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  return t6;
}
function _temp() {
  return createInitialInlineGroupBuilderState();
}